/* eslint-disable @typescript-eslint/no-misused-promises */
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Box,
  useDisclosure,
  Icon,
  ModalContainer,
} from '@ouster/webviz-react-components';

import React, { useState } from 'react';
import {
  RiProfileLine,
  RiTeamLine,
  RiFileShield2Line,
  RiBarChartBoxLine,
} from 'react-icons/ri';
import { useNavigate } from 'react-router';

/* 
  comment all password change related below, this feature will be ready in release 3
*/
import HtmlEula from './HtmlEula';
import Profile from './Profile';
import UserOrgSwitch from './UserOrgSwitch';
import useLogout from '../../hooks/useLogout';
import { useStoreActions, useStoreState } from '../../reducers';
import { useFeedbackForm } from '../../utils/customHooks';

type MenuAction =
  | 'PROFILE'
  | 'ORGANIZATION'
  | 'EULA'
  | 'FEEDBACK'
  | 'SIGN-OUT'
  | 'ADMIN-DASHBOARD';

type TMenuItem = {
  label: string;
  action: MenuAction;
  icon: React.ReactElement;
};

type ModalMenuAction = 'PROFILE' | 'EULA';

type ModalMenuItem = TMenuItem & {
  action: ModalMenuAction;
};

type ModalProperties = {
  header: React.ReactNode;
  footer: React.ReactNode;
  content: React.ReactNode;
};

const MENU_ITEMS: TMenuItem[] = [
  {
    label: 'Admin Dashboard',
    action: 'ADMIN-DASHBOARD',
    icon: <RiBarChartBoxLine />,
  },
  { label: 'Manage Profile', action: 'PROFILE', icon: <RiProfileLine /> },
  { label: 'EULA', action: 'EULA', icon: <RiFileShield2Line /> },
  {
    label: 'Organization Settings',
    action: 'ORGANIZATION',
    icon: <RiTeamLine />,
  },
  { label: 'Feedback', action: 'FEEDBACK', icon: <Icon icon="feedback" /> },
  { label: 'Sign Out', action: 'SIGN-OUT', icon: <Icon icon="logout" /> },
];

const MenuHeader = ({ displayName }: { displayName?: string }) => (
  <Box
    fontSize={16}
    fontWeight={500}
    py={2}
    mb={2}
    marginInline={6}
    color="white"
    width="200px"
    whiteSpace="nowrap"
    textOverflow="ellipsis"
    overflow="hidden"
  >
    Hi, {displayName}
  </Box>
);

export default function NavUser() {
  const { appUser, curUserOrg, hasMultiOrgs, userEulaLink, isOusterUser } =
    useStoreState((storeState) => storeState.user);
  const { fetchUserEulaLink } = useStoreActions((actions) => actions.user);

  const {
    isOpen: isDialogOpen,
    onOpen: onDialogOpen,
    onClose: onDialogClose,
  } = useDisclosure();
  const {
    isOpen: isMenuOpen,
    onClose: onMenuClose,
    onOpen: onMenuOpen,
  } = useDisclosure();

  const [selectedMenuItem, setSelectedMenuItem] =
    useState<ModalMenuItem | null>(null);
  const navigate = useNavigate();
  const feedbackFormLink = useFeedbackForm();
  const logout = useLogout();

  async function handleMenuClick(menuItem: TMenuItem) {
    const openSelectedModal = (modalMenuItem: ModalMenuItem) => {
      setSelectedMenuItem(modalMenuItem);
      onDialogOpen();
    };

    const actionHandlers: Record<MenuAction, () => Promise<void> | void> = {
      FEEDBACK: () => {
        window.open(feedbackFormLink, '_blank');
      },
      'SIGN-OUT': () => {
        logout();
      },
      'ADMIN-DASHBOARD': () => {
        navigate('/admin_dashboard');
      },
      ORGANIZATION: () => {
        navigate('/organization');
      },
      PROFILE: () => {
        openSelectedModal(menuItem as ModalMenuItem);
      },
      EULA: async () => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        await fetchUserEulaLink(appUser!.identity_id);
        openSelectedModal(menuItem as ModalMenuItem);
      },
    };

    await actionHandlers[menuItem.action]();
  }

  const getModalProperties = (
    selectedItem: ModalMenuItem | null
  ): ModalProperties => {
    if (!selectedItem) return { header: null, footer: null, content: null };
    const modalConfig: Record<ModalMenuAction, ModalProperties> = {
      PROFILE: {
        header: 'Profile',
        footer: null,
        content: <Profile handleClose={onDialogClose} />,
      },
      EULA: {
        header: 'View EULA',
        footer: null, // No footer for Profile
        content: (
          <HtmlEula handleClose={onDialogClose} eulaLink={userEulaLink} />
        ),
      },
    };

    return modalConfig[selectedItem.action];
  };
  const { header, footer, content } = getModalProperties(selectedMenuItem);

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        columnGap={2}
        paddingBlock="6px"
        paddingInline="10px"
        border={1}
        borderColor="gray.600"
        borderStyle="solid"
        borderRadius="8px"
        data-testid="nav-user-root"
        _hover={{
          bgColor: 'gray.800',
          borderColor: 'gray.500',
        }}
      >
        <Menu
          placement="top-end"
          offset={[5, 15]}
          autoSelect={false}
          onClose={onMenuClose}
          isOpen={isMenuOpen}
        >
          <MenuButton
            onClick={onMenuOpen}
            p={0}
            bg="transparent"
            data-testid="nav-user-menu-btn"
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                as="span"
                display={{ base: 'none', md: 'inline-block' }}
                maxWidth="15ch"
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                verticalAlign="bottom"
                data-testid="nav-user-current-org"
              >
                {curUserOrg?.name}
              </Box>
              <Icon
                bg="blue.400"
                borderRadius="100%"
                color="white"
                icon="manageAccount"
                p={1}
                size={8}
              />
            </Box>
          </MenuButton>
          <MenuList
            data-testid="nav-user-menu-list"
            rowGap={2}
            maxHeight="80vh"
            overflowY="auto"
          >
            <MenuHeader displayName={appUser?.name || appUser?.email} />
            <MenuDivider borderColor="gray.600" />

            {hasMultiOrgs && (
              <>
                <UserOrgSwitch closeUserMenu={onMenuClose} />
                <MenuDivider borderColor="gray.600" />
              </>
            )}

            {MENU_ITEMS.map((item) => {
              // Skip rendering "ADMIN-DASHBOARD" if conditions are not met
              if (
                item.action === 'ADMIN-DASHBOARD' &&
                (!isOusterUser || curUserOrg?.role === 'BASIC')
              ) {
                return null;
              }
              return (
                <React.Fragment key={item.action}>
                  {' '}
                  {item.action === 'SIGN-OUT' && (
                    <MenuDivider
                      key={`${item.action}-divider`}
                      borderColor="gray.600"
                    />
                  )}
                  <MenuItem
                    key={item.action}
                    onClick={() => handleMenuClick(item)}
                    style={{
                      fontSize: 14,
                      paddingBlock: '0.75rem',
                      paddingInline: '1.25rem',
                      marginBlock: '0.25rem',
                      columnGap: '0.5rem',
                    }}
                    _hover={{ bgColor: 'var(--color-blast-off-blue)' }}
                    _focus={{ bgColor: 'var(--color-blast-off-blue)' }}
                    data-testid={`nav-user-menu-item-${item.action.toLowerCase()}`}
                  >
                    <span style={{ fontSize: 20 }}>{item.icon}</span>
                    {item.label}
                  </MenuItem>
                  {item.action === 'ADMIN-DASHBOARD' && (
                    <MenuDivider
                      key={`${item.action}-divider`}
                      borderColor="gray.600"
                    />
                  )}
                </React.Fragment>
              );
            })}
          </MenuList>
        </Menu>
      </Box>

      <ModalContainer
        isOpen={isDialogOpen}
        onClose={onDialogClose}
        header={header}
        footer={footer}
        content={content}
      />
    </>
  );
}
